import moment from "moment";
import { baseURL } from "../../../../util/server";

export const webChatFileUrl = (fileNameOrUrl) => {
  if (!fileNameOrUrl) return null;
  const raw = String(fileNameOrUrl).trim();
  if (raw.startsWith("blob:")) return raw;

  // Always serve via /web-chat/downloadFile (storage folder is still "webchat")
  let name = raw;
  if (/^https?:\/\//i.test(raw) || raw.includes("/downloadFile/")) {
    try {
      const path = raw.includes("://") ? new URL(raw).pathname : raw;
      name = decodeURIComponent(path.split("/").pop() || "");
    } catch {
      name = decodeURIComponent(raw.split("/").pop() || "");
    }
  }
  if (!name) return null;
  return `${baseURL}/web-chat/downloadFile/${encodeURIComponent(name)}`;
};

const isImageFile = (name = "") =>
  /\.(png|jpe?g|gif|webp|bmp|svg)$/i.test(String(name));

const isHttpUrl = (value = "") =>
  /^https?:\/\//i.test(String(value)) || String(value).includes("/downloadFile/");

/** Prefer original name from content; fall back to a short label for stored UUID URLs. */
const resolveAttachmentDisplayName = (attachment, content) => {
  const contentTrim = String(content || "").trim();
  if (contentTrim && !isHttpUrl(contentTrim)) {
    return contentTrim.split(",")[0].trim();
  }
  const raw = decodeURIComponent(String(attachment || "").split("/").pop() || "");
  if (!raw) return "Attachment";
  if (/spreadsheetml|sheet/i.test(raw)) return "Spreadsheet.xlsx";
  if (/wordprocessingml|officedocument\.word/i.test(raw)) return "Document.docx";
  if (/presentationml|powerpoint/i.test(raw)) return "Presentation.pptx";
  if (/\.pdf$/i.test(raw) || /application\.pdf/i.test(raw)) return "Document.pdf";
  if (/^webchat_[0-9a-f-]{36}/i.test(raw)) return "Attachment";
  return raw;
};

export const formatWebChatTime = (value) => {
  if (!value) return "";
  const m = moment(value);
  if (!m.isValid()) return "";
  const today = moment().startOf("day");
  if (m.isSameOrAfter(today)) return m.format("h:mm A");
  if (m.isSameOrAfter(today.clone().subtract(1, "day"))) return "Yesterday";
  if (m.isSame(moment(), "year")) return m.format("MMM D");
  return m.format("MMM D, YYYY");
};

/** Friendly sidebar / list preview for last message text. */
export const formatWebChatLastMessagePreview = (lastMessage, lastMessageSenderType) => {
  const text = String(lastMessage || "").trim();
  if (!text) return "";
  if (/^\[IMAGE\s+attachment\]$/i.test(text) || /^photo$/i.test(text)) return "Photo";
  if (/^\[FILE\s+attachment\]$/i.test(text) || /^\[DOCUMENT\s+attachment\]$/i.test(text)) {
    return "Attachment";
  }
  // Raw camera / image file names previously stored as content
  if (/^camera_capture_/i.test(text) || /\.(png|jpe?g|gif|webp|bmp|heic)$/i.test(text)) {
    return "Photo";
  }
  return text;
};

export const mapWebChatListItemToUi = (item) => {
  if (!item) return null;
  const source = (item.source || "").toUpperCase();
  const channel =
    source === "WHATSAPP" || item.whatsappSynced ? "whatsapp" : "app";
  const unread = Number(item.unreadCount) || 0;
  return {
    id: item.chatId,
    chatId: item.chatId,
    patientName: item.clientName || item.displayTitle || "Client",
    petName: item.petName || "",
    avatarInitials: item.initials || "CH",
    role: item.role || "",
    lastMessage: formatWebChatLastMessagePreview(item.lastMessage, item.lastMessageSenderType),
    lastMessageTime: formatWebChatTime(item.lastMessageAt),
    unreadCount: unread,
    channel,
    isOnline: true,
    tab: unread > 0 ? "unread" : "all",
    favorite: !!item.favorite,
    clientMobile: item.clientMobile || "",
    clientImage: item.clientImage || "",
    petImage: item.petImage || "",
    clientUserId: item.clientUserId,
    petId: item.petId,
    clinicId: item.clinicId,
    whatsappSynced: !!item.whatsappSynced,
    source: item.source || "WEB",
    displayTitle: item.displayTitle,
    raw: item,
  };
};

export const mapWebChatMessageToUi = (msg, currentUserId) => {
  if (!msg) return null;
  const senderType = (msg.senderType || "").toUpperCase();
  const isMine =
    currentUserId != null &&
    msg.senderId != null &&
    String(msg.senderId) === String(currentUserId);

  let sender = "patient"; // incoming (other party)
  if (senderType === "SYSTEM") {
    sender = "system";
  } else if (isMine) {
    sender = "doctor"; // outgoing (current user) — keeps existing bubble styles
  }

  const typeRaw = (msg.messageType || "TEXT").toUpperCase();
  const attachments = Array.isArray(msg.attachments) ? msg.attachments : [];
  const firstFile = attachments[0];
  const imageFromAttach = firstFile && isImageFile(firstFile);

  let type = "text";
  if (typeRaw === "IMAGE" || typeRaw === "PHOTO" || imageFromAttach) {
    type = "image";
  } else if (
    typeRaw === "DOCUMENT" ||
    typeRaw === "FILE" ||
    (attachments.length > 0 && !imageFromAttach)
  ) {
    type = "document";
  }

  const status = (msg.status || "sent").toLowerCase();
  const created = msg.createdDate;
  const senderName =
    msg.senderName || (isMine ? "You" : senderType === "STAFF" ? "Staff" : "Client");
  const senderInitial =
    String(senderName).trim().charAt(0).toUpperCase() || (isMine ? "Y" : "C");

  const ui = {
    id: msg.messageId,
    messageId: msg.messageId,
    chatId: msg.chatId,
    senderId: msg.senderId,
    sender,
    isMine,
    senderName,
    senderInitial,
    type,
    content:
      type === "image"
        ? webChatFileUrl(firstFile) || msg.content
        : msg.content || "",
    timestamp: moment(created).isValid()
      ? moment(created).format("h:mm A")
      : "",
    date: moment(created).isValid()
      ? moment(created).calendar(null, {
          sameDay: "[Today]",
          lastDay: "[Yesterday]",
          lastWeek: "MMM D",
          sameElse: "MMM D, YYYY",
        })
      : "",
    status: status === "read" ? "read" : status === "delivered" ? "delivered" : "sent",
    raw: msg,
  };

  if (type === "document" && firstFile) {
    const displayName = resolveAttachmentDisplayName(firstFile, msg.content);
    ui.fileName = displayName;
    ui.fileType = (String(displayName).split(".").pop() || "FILE").toUpperCase();
    ui.fileSize = "";
    ui.content = webChatFileUrl(firstFile);
  }

  return ui;
};

export const phoneKey = (value) => {
  const digits = String(value || "").replace(/\D/g, "");
  if (!digits) return "";
  return digits.length > 10 ? digits.slice(-10) : digits;
};

/** Customer phone on an inbox row. Outbound rows use `to`, replies use `from`. */
export const whatsAppCustomerPhone = (msg) => {
  const direction = String(msg?.direction || "in").toLowerCase();
  return phoneKey(direction === "out" ? msg?.to : msg?.from);
};

export const mapWhatsAppInboxMessageToUi = (msg) => {
  if (!msg) return null;
  const outbound = String(msg.direction || "in").toLowerCase() === "out";
  const seconds = Number(msg.timestamp);
  const created =
    Number.isFinite(seconds) && seconds > 0 ? moment.unix(seconds) : moment();
  const senderName = outbound ? "You" : msg.name || msg.from || "WhatsApp";
  return {
    id: msg.id || `${msg.timestamp}-${msg.body}`,
    messageId: msg.id,
    sender: outbound ? "doctor" : "patient",
    isMine: outbound,
    senderName,
    senderInitial: String(senderName).trim().charAt(0).toUpperCase() || "W",
    type: "text",
    content: msg.body || "",
    timestamp: created.format("h:mm A"),
    date: created.calendar(null, {
      sameDay: "[Today]",
      lastDay: "[Yesterday]",
      lastWeek: "MMM D",
      sameElse: "MMM D, YYYY",
    }),
    status: (msg.status || "sent").toLowerCase(),
    raw: msg,
  };
};

export const mapWhatsAppThreads = (inbox = []) => {
  const groups = new Map();
  inbox.forEach((msg) => {
    const key = whatsAppCustomerPhone(msg);
    if (!key) return;
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(msg);
  });
  return Array.from(groups.entries()).map(([key, messages]) => {
    const latest = messages[messages.length - 1];
    const inbound = messages.find((item) => String(item.direction || "in") !== "out");
    const name = inbound?.name || latest?.from || latest?.to || key;
    const phone = String(latest?.direction || "in").toLowerCase() === "out"
      ? latest?.to
      : latest?.from || latest?.to;
    return {
      id: `wa-${key}`,
      chatId: `wa-${key}`,
      patientName: name,
      petName: "",
      avatarInitials: String(name).trim().charAt(0).toUpperCase() || "W",
      role: "",
      lastMessage: latest?.body || "",
      lastMessageTime: formatWebChatTime(
        Number(latest?.timestamp) > 0
          ? moment.unix(Number(latest.timestamp)).toISOString()
          : null
      ),
      unreadCount: 0,
      channel: "whatsapp",
      isOnline: true,
      tab: "all",
      favorite: false,
      clientMobile: phone || key,
      clientImage: "",
      petImage: "",
      clientUserId: null,
      petId: null,
      source: "WHATSAPP",
      displayTitle: name,
      sortTime: Number(latest?.timestamp) || 0,
    };
  }).sort((a, b) => (b.sortTime || 0) - (a.sortTime || 0));
};

export const mapActiveChatToPatientDetails = (chat) => {
  if (!chat) return null;
  return {
    name: chat.patientName,
    phone: chat.clientMobile || "",
    email: "",
    address: "",
    avatarInitials: chat.avatarInitials,
    channel: chat.channel,
  };
};

export const mapActiveChatToPetDetails = (chat) => {
  if (!chat || !chat.petName) return null;
  return {
    name: chat.petName,
    breed: "",
    species: "",
    avatarInitials: chat.avatarInitials?.slice(-1) || "P",
    petId: chat.petId,
  };
};

export const resolveClinicIdForWebSocket = (profile, role) => {
  if (!profile) return null;
  if (role === "ROLE_CLINIC") return profile.id;
  return (
    profile.activeBranchId ||
    profile.hospitalId ||
    Number(localStorage.getItem("hospitalId")) ||
    profile.createdBy ||
    profile.id
  );
};
